import React, { useState, useMemo } from 'react';
import SlideShowWords from './SlideShowWords';

type SlideImage = string | { default: string };

interface ImageSlideDeckProps {
    images: Record<string, SlideImage> & { name?: string; numKeys?: number };
}

function ImageSlideDeck({ images }: ImageSlideDeckProps) {
    const [currIndex, setCurrIndex] = useState(0);
    // Generate index keys from images (ignore responsive variants like -800/-1200)
    const indexKeys = useMemo(() => {
        const allKeys = Object.keys(images).filter((key) => {
            if (key === 'name' || key === 'numKeys') return false;
            if (!key.endsWith('.webp')) return false;
            if (key.includes('-800') || key.includes('-1200') || key.includes('-1600')) return false;
            return true;
        });

        const namePrefix = images["name"] ? `${images["name"]}` : '';
        const filtered = namePrefix ? allKeys.filter((key) => key.startsWith(namePrefix)) : allKeys;

        return filtered.sort((a, b) => {
            const matchA = a.match(/(\d+)\.webp$/);
            const matchB = b.match(/(\d+)\.webp$/);
            if (matchA && matchB) {
                return Number(matchA[1]) - Number(matchB[1]);
            }
            return a.localeCompare(b);
        });
    }, [images]);

    const changeSlides = (delta) => {
        let temp = (currIndex + delta) % indexKeys.length;
        if (temp < 0) temp = indexKeys.length + temp;
        setCurrIndex(temp);
    };

    const currentKey = indexKeys[currIndex];
    const currentImage = images[currentKey];
    const currentSrc = currentImage?.default || currentImage;
    const isFirstSlide = currIndex === 0;
    const isHome = images["name"] === "home";
    const altText = `${images["name"] ?? "tour"} slide ${currIndex + 1}`;
    const baseName = currentKey?.replace('.webp', '');
    const smallKey = baseName ? `${baseName}-800.webp` : '';
    const mediumKey = baseName ? `${baseName}-1200.webp` : '';
    const largeKey = currentKey || '';
    const smallImage = images[smallKey];
    const mediumImage = images[mediumKey];
    const largeImage = images[largeKey];
    const smallSrc = typeof smallImage === 'string' ? smallImage : smallImage?.default;
    const mediumSrc = typeof mediumImage === 'string' ? mediumImage : mediumImage?.default;
    const largeSrc = typeof largeImage === 'string' ? largeImage : largeImage?.default;
    const srcSetCandidates = [
        { url: smallSrc, w: 800 },
        { url: mediumSrc, w: 1200 },
        { url: largeSrc, w: 1600 },
    ];
    const srcSet = srcSetCandidates
        .map(({ url, w }) => (url ? `${url} ${w}w` : null))
        .filter(Boolean)
        .join(', ');

    const prefersSmall = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 600px)').matches;
    const preferredSrc = prefersSmall && smallSrc ? smallSrc : (largeSrc || currentSrc);

    return (
        <div className="slideShowContainer">
            {indexKeys.length > 1 && (
                <div>
                    <span className="next" onClick={() => changeSlides(1)} />
                    <span className="prev" onClick={() => changeSlides(-1)} />
                </div>
            )}
            {isHome && (
                <div className="slideShowText">
                    <SlideShowWords text="Dream. Explore." />
                    <SlideShowWords text="Discover." />
                    <SlideShowWords text="Southern Belize." />
                </div>
            )}
            <picture>
                {smallSrc && (
                    <source media="(max-width: 600px)" srcSet={`${smallSrc} 800w`} type="image/webp" />
                )}
                {mediumSrc && (
                    <source media="(max-width: 1024px)" srcSet={`${mediumSrc} 1200w`} type="image/webp" />
                )}
                {largeSrc && (
                    <source media="(min-width: 1025px)" srcSet={`${largeSrc} 1600w`} type="image/webp" />
                )}
                <img
                    className="slideImage"
                    alt={altText}
                    src={preferredSrc}
                    srcSet={srcSet || undefined}
                    sizes="(max-width: 600px) 100vw, (max-width: 1024px) 100vw, 1600px"
                    width={1600}
                    height={900}
                    decoding="async"
                    loading={isFirstSlide ? "eager" : "lazy"}
                    fetchpriority={isHome && isFirstSlide ? "high" : "auto"}
                />
            </picture>
        </div>
    );
}

export default ImageSlideDeck;
